import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import { format, parseISO, getISOWeek } from "date-fns";
import { de } from "date-fns/locale";
import {
  ArrowLeft,
  Save,
  Users,
  Plus,
  Trash2,
  Sun,
  Cloud,
  CloudRain,
  Snowflake,
  Sparkles,
  ListTodo,
  AlertTriangle,
  Car,
  BedDouble,
  Wallet,
  ImagePlus,
  Camera,
  X,
  Loader2,
  RefreshCw,
  Download,
  Pen,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import SignaturePad from "../components/SignaturePad";

const API = "/api";

// Hängt Auth-Token an Photo-URL für <img src=> Anzeige
function photoSrc(ph) {
  if (!ph?.url) return "";
  const t = localStorage.getItem("token") || "";
  if (!t) return ph.url;
  const sep = ph.url.includes("?") ? "&" : "?";
  return `${ph.url}${sep}token=${encodeURIComponent(t)}`;
}

const WEATHER_OPTIONS = [
  { value: "sonnig", label: "Sonnig", icon: Sun },
  { value: "bewoelkt", label: "Bewölkt", icon: Cloud },
  { value: "regen", label: "Regen", icon: CloudRain },
  { value: "schnee", label: "Schnee", icon: Snowflake },
];

const PAUSE_OPTIONS = [0, 30, 60];

function todayIso() {
  return new Date().toISOString().split("T")[0];
}

function minutesBetween(start, end, pause = 0) {
  if (!start || !end) return 0;
  try {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let mins = eh * 60 + em - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60;
    return Math.max(0, mins - (pause || 0));
  } catch {
    return 0;
  }
}

function fmtH(mins) {
  if (!mins) return "0,00 h";
  const h = mins / 60;
  return `${h.toFixed(2).replace(".", ",")} h`;
}

function vmaForMinutes(mins) {
  if (mins >= 24 * 60) return 28;
  if (mins >= 8 * 60) return 14;
  return 0;
}

// Entfernt wiederholte Titel-Header am Anfang der Beschreibung.
// Beispiel: title="Elektro", description="Elektro\nElektro\n- LED" -> "- LED"
// Idempotent: bei sauberer Beschreibung wird nichts geändert.
function stripDuplicateTitleFromDescription(title, description) {
  if (!description) return "";
  const desc = String(description);
  const t = (title || "").trim();
  if (!t) return desc;
  let out = desc;
  let safety = 8;
  while (safety-- > 0) {
    const leading = out.replace(/^[\s\r\n]+/, "");
    if (leading === t) {
      out = "";
      break;
    }
    if (leading.startsWith(t + "\n") || leading.startsWith(t + "\r\n")) {
      out = leading.slice(t.length).replace(/^[\r\n]+/, "");
      continue;
    }
    break;
  }
  return out;
}

// ============ PERSONNEL ROW ============
function PersonRow({ entry, users, onChange, onRemove }) {
  const netMinutes = minutesBetween(entry.work_start, entry.work_end, entry.break_minutes);
  return (
    <div
      className="rounded-xl border border-gray-200 bg-white p-3 md:p-4 space-y-3"
      data-testid={`btb-person-row-${entry._key}`}
    >
      {/* Top: Mitarbeiter + Remove */}
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <Label className="text-xs text-gray-500">Mitarbeiter</Label>
          <Select
            value={entry.user_id || ""}
            onValueChange={(v) => {
              const u = users.find((x) => x.id === v);
              onChange({
                ...entry,
                user_id: v,
                name: u?.name || entry.name,
                company: u?.company || entry.company,
              });
            }}
          >
            <SelectTrigger
              className="mt-1 h-11"
              data-testid={`btb-person-select-${entry._key}`}
            >
              <SelectValue placeholder="Mitarbeiter wählen" />
            </SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                  {u.company ? ` · ${u.company}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="mt-6 p-2 text-red-500 hover:bg-red-50 rounded-lg"
          data-testid={`btb-person-remove-${entry._key}`}
          aria-label="Mitarbeiter entfernen"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Times */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div>
          <Label className="text-xs text-gray-500">Start</Label>
          <Input
            type="time"
            value={entry.work_start || ""}
            onChange={(e) => onChange({ ...entry, work_start: e.target.value })}
            className="mt-1 h-11"
            data-testid={`btb-person-start-${entry._key}`}
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Ende</Label>
          <Input
            type="time"
            value={entry.work_end || ""}
            onChange={(e) => onChange({ ...entry, work_end: e.target.value })}
            className="mt-1 h-11"
            data-testid={`btb-person-end-${entry._key}`}
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Pause</Label>
          <div className="mt-1 flex gap-1">
            {PAUSE_OPTIONS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => onChange({ ...entry, break_minutes: p })}
                className={`flex-1 h-11 rounded-lg border text-sm font-medium ${
                  entry.break_minutes === p
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
                data-testid={`btb-person-pause-${entry._key}-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Netto Arbeit</Label>
          <div className="mt-1 h-11 px-3 rounded-lg bg-gray-50 border border-gray-200 flex items-center font-semibold text-gray-900">
            {fmtH(netMinutes)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ ACTIVITY ROW ============
function ActivityCard({ idx, activity, onChange, onRemove, projectId, reportDate, areaSuggestions = [] }) {
  const [aiBusy, setAiBusy] = useState(false);

  const onFormulate = async () => {
    if (!activity.description?.trim()) {
      toast.info("Bitte zuerst Stichpunkte / Notizen eingeben");
      return;
    }
    setAiBusy(true);
    try {
      const res = await authFetch(
        `${API}/projects/${projectId}/daily-reports/formulate-activity`,
        {
          method: "POST",
          body: JSON.stringify({
            title: activity.title || "",
            text: activity.description,
            report_date: reportDate || "",
            area_location: activity.area_location || "",
          }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (data?.text) {
        onChange({ ...activity, description: data.text });
        toast.success("Text formuliert");
      } else {
        toast.error("Kein Text zurückgegeben");
      }
    } catch (e) {
      toast.error("KI-Formulierung fehlgeschlagen");
    } finally {
      setAiBusy(false);
    }
  };

  const areaDatalistId = `btb-area-suggestions-${idx}`;

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white p-3 md:p-4 space-y-3"
      data-testid={`btb-activity-${idx}`}
    >
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
          <ListTodo className="w-4 h-4 text-blue-600" />
        </div>
        <div className="flex-1">
          <Input
            value={activity.title || ""}
            onChange={(e) => onChange({ ...activity, title: e.target.value })}
            placeholder="Titel der Tätigkeit (z. B. Elektroinstallation EG)"
            className="font-medium"
            data-testid={`btb-activity-title-${idx}`}
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
          data-testid={`btb-activity-remove-${idx}`}
          aria-label="Tätigkeit entfernen"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Bereich / Ort im Projekt (optional, Autocomplete via datalist) */}
      <div>
        <Label className="text-xs text-gray-500">Bereich / Ort im Projekt (optional)</Label>
        <Input
          value={activity.area_location || ""}
          onChange={(e) => onChange({ ...activity, area_location: e.target.value })}
          placeholder="z. B. Hinterer Technik Bereich, Halle 1, UV 1 ..."
          list={areaDatalistId}
          className="mt-1"
          data-testid={`btb-activity-area-${idx}`}
        />
        {areaSuggestions.length > 0 && (
          <datalist id={areaDatalistId}>
            {areaSuggestions.map((a) => <option key={a} value={a} />)}
          </datalist>
        )}
      </div>

      <Textarea
        value={activity.description || ""}
        onChange={(e) => onChange({ ...activity, description: e.target.value })}
        placeholder={
          "Stichpunkte oder Beschreibung – Deutsch, Englisch oder Slowakisch:\n" +
          "- LED-Leuchten montiert\n" +
          "- installed lights in back technical area\n" +
          "- namontovali sme svietidla"
        }
        rows={4}
        className="resize-y"
        data-testid={`btb-activity-desc-${idx}`}
      />

      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500">
          Stichpunkte auch auf <strong>Deutsch, Englisch oder Slowakisch</strong> möglich – die KI formuliert daraus sauberes Deutsch.
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onFormulate}
          disabled={aiBusy}
          data-testid={`btb-activity-ai-${idx}`}
        >
          {aiBusy ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2" />
          )}
          Text automatisch formulieren
        </Button>
      </div>
    </div>
  );
}

// ============ MAIN ============
export default function BautagesberichtForm() {
  const { projectId, reportId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const dateParam = searchParams.get("date") || todayIso();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [date, setDate] = useState(dateParam);

  // Form state
  const [weather, setWeather] = useState("sonnig");
  const [temperature, setTemperature] = useState("");
  const [personnel, setPersonnel] = useState([]); // {_key, user_id, name, company, work_start, work_end, break_minutes}
  const [activities, setActivities] = useState([]); // {_key, title, description}
  const [disruptions, setDisruptions] = useState("");
  const [disruptionAreaLocation, setDisruptionAreaLocation] = useState("");
  const [areaSuggestions, setAreaSuggestions] = useState([]);
  const [notes, setNotes] = useState("");

  // Optional sections
  const [trips, setTrips] = useState([]); // {_key, user_id, user_name, hin_start, hin_end, hin_km, rueck_start, rueck_end, rueck_km}
  const [accommodationActive, setAccommodationActive] = useState(false);
  const [accommodations, setAccommodations] = useState([]); // from prefill
  const [photos, setPhotos] = useState([]); // {id, url, filename}
  const [disruptionPhotos, setDisruptionPhotos] = useState([]); // Fotos zur Störung
  const [includeDisruptionPhotosInBa, setIncludeDisruptionPhotosInBa] = useState(null); // null=auto
  const [reportNumber, setReportNumber] = useState("");
  const [includeTripsInPdf, setIncludeTripsInPdf] = useState(false);
  const [includeVmaInPdf, setIncludeVmaInPdf] = useState(false);
  const [includeAccommodationInPdf, setIncludeAccommodationInPdf] = useState(false);
  // VOB-Hinweis & Störungs-Sichtbarkeit (null = auto)
  const [showVobNotice, setShowVobNotice] = useState(null);
  const [includeDisruptionInPdf, setIncludeDisruptionInPdf] = useState(null);
  // Abnahme / Unterschriften
  const [signatureEmployeeName, setSignatureEmployeeName] = useState("");
  const [signatureCustomerName, setSignatureCustomerName] = useState("");
  const [signatureLocation, setSignatureLocation] = useState("");
  const [signatureDate, setSignatureDate] = useState("");
  const [signatureEmployeeImage, setSignatureEmployeeImage] = useState("");
  const [signatureCustomerImage, setSignatureCustomerImage] = useState("");
  const [disruptionBusy, setDisruptionBusy] = useState(false);

  const [accordionOpen, setAccordionOpen] = useState([]);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const disruptionFileInputRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [disruptionPhotoBusy, setDisruptionPhotoBusy] = useState(false);
  const [prefillBusy, setPrefillBusy] = useState(false);

  // Granulare Bautagesbericht-Rechte (vom Backend geladen)
  const [drPerms, setDrPerms] = useState(null);
  const canEdit = !!(drPerms?.create || drPerms?.edit);
  const canSave = reportId ? !!drPerms?.edit : !!drPerms?.create;
  // Legacy-Anzeige für Admin-Sections (Auto-Create etc.)
  const isAdmin =
    user?.permissions?.can_approve_entries || user?.role === "admin";

  // ===== Load project + users =====
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId) return;
      setLoading(true);
      try {
        const [projRes, usersRes, permsRes] = await Promise.all([
          authFetch(`${API}/projects`),
          authFetch(`${API}/users`),
          authFetch(`${API}/projects/${projectId}/daily-reports/permissions`),
        ]);
        if (projRes.ok) {
          const projects = await projRes.json();
          const proj = projects.find((p) => p.id === projectId);
          if (!cancelled) setProject(proj || null);
        }
        if (usersRes.ok) {
          const u = await usersRes.json();
          if (!cancelled) setUsers(Array.isArray(u) ? u : u.users || []);
        }
        if (permsRes.ok) {
          const pj = await permsRes.json();
          if (!cancelled) setDrPerms(pj.permissions || null);
        }

        // If editing existing report: load it
        if (reportId) {
          const rRes = await authFetch(
            `${API}/projects/${projectId}/daily-reports/${reportId}`
          );
          if (rRes.ok) {
            const r = await rRes.json();
            if (!cancelled) hydrateFromReport(r);
          }
        } else {
          // Prefill from Stundenkonto
          await runPrefill(dateParam, true);
        }
      } catch {
        toast.error("Fehler beim Laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, reportId]);

  // Ort + Datum für Unterschriftsbereich automatisch setzen (nur wenn leer)
  // Quelle: Projektort (city) > Projektaddress > Projektname (1. Komma-Part) > manuelle Eingabe
  useEffect(() => {
    if (signatureLocation || !project) return;
    let city = "";
    const addr = project.address;
    if (typeof addr === "string") {
      // versuche, Stadt aus "Straße, PLZ Stadt" zu extrahieren
      const parts = addr.split(",").map((p) => p.trim());
      const last = parts[parts.length - 1] || "";
      const bits = last.split(" ").filter(Boolean);
      // PLZ am Anfang: nimm den Rest als Stadt
      if (bits.length >= 2 && /^\d{4,5}$/.test(bits[0])) {
        city = bits.slice(1).join(" ");
      } else {
        city = last;
      }
    } else if (addr && typeof addr === "object") {
      city = addr.city || addr.ort || "";
    }
    if (!city) city = project.location || project.einsatzort || "";
    // Fallback: erster Komma-Teil des Projektnamens (oft "Stadt, Beschreibung")
    if (!city && project.name) {
      const first = project.name.split(",")[0].trim();
      if (first && first.length <= 40) city = first;
    }
    if (city) setSignatureLocation(city);
  }, [project, signatureLocation]);

  // Signatur-Datum auf Berichtdatum synchronisieren, solange User nichts manuell gesetzt hat
  useEffect(() => {
    if (!signatureDate && date) setSignatureDate(date);
  }, [date, signatureDate]);

  // Bereiche/Orte für Autocomplete laden (pro Projekt)
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/projects/${projectId}/daily-reports/area-locations`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setAreaSuggestions(Array.isArray(data?.areas) ? data.areas : []);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  function hydrateFromReport(r) {
    setDate(r.date);
    setWeather(r.weather || "sonnig");
    setTemperature(r.temperature_celsius != null ? String(r.temperature_celsius) : "");
    setNotes(r.notes || "");
    setDisruptions(r.disruptions || "");
    setDisruptionAreaLocation(r.disruption_area_location || "");
    setAccommodationActive(!!r.accommodation_active);
    setAccommodations(r.accommodations || []);
    setReportNumber(r.report_number || "");

    const p = (r.personnel || []).map((x, i) => ({ _key: `p-${i}`, ...x }));
    if (p.length === 0 && r.employees_on_site?.length) {
      setPersonnel(
        r.employees_on_site.map((e, i) => ({
          _key: `p-${i}`,
          user_id: e.user_id,
          name: e.user_name,
          work_start: r.work_start || "",
          work_end: r.work_end || "",
          break_minutes: 30,
        }))
      );
    } else {
      setPersonnel(p);
    }

    setActivities(
      (r.activities || []).map((a, i) => {
        const title = a.source_task_title || a.title || "";
        const cleanDesc = stripDuplicateTitleFromDescription(title, a.description || "");
        return {
          _key: `a-${i}`,
          title,
          description: cleanDesc,
          area_location: a.area_location || "",
          source_task_id: a.source_task_id,
          source_task_title: a.source_task_title,
          employee_id: a.employee_id,
          employee_name: a.employee_name,
        };
      })
    );

    setTrips((r.trips || []).map((t, i) => ({ _key: `t-${i}`, ...t })));
    setPhotos(r.photos || []);
    setDisruptionPhotos(r.disruption_photos || []);
    setIncludeTripsInPdf(r.include_trips_in_pdf === true);
    setIncludeVmaInPdf(r.include_vma_in_pdf === true);
    setIncludeAccommodationInPdf(r.include_accommodation_in_pdf === true);
    // VOB-Hinweis & Störungs-Sichtbarkeit (null = auto)
    setShowVobNotice(r.show_vob_notice === undefined ? null : r.show_vob_notice);
    setIncludeDisruptionInPdf(r.include_disruption_in_pdf === undefined ? null : r.include_disruption_in_pdf);
    setIncludeDisruptionPhotosInBa(r.include_disruption_photos_in_ba === undefined ? null : r.include_disruption_photos_in_ba);
    // Abnahme / Unterschriften (Fallbacks für ältere Berichte)
    setSignatureEmployeeName(r.signature_employee || "");
    setSignatureCustomerName(r.signature_customer || "");
    setSignatureLocation(r.signature_location || "");
    setSignatureDate(r.signature_date || r.date || "");
    setSignatureEmployeeImage(r.signature_employee_image || "");
    setSignatureCustomerImage(r.signature_customer_image || "");

    const open = [];
    if ((r.trips || []).length) open.push("trips");
    if (r.accommodation_active || (r.accommodations || []).length) open.push("acc");
    if ((r.vma_total_eur || 0) > 0) open.push("vma");
    setAccordionOpen(open);
  }

  // ===== Prefill from Stundenkonto =====
  async function runPrefill(forDate, isInitial = false) {
    if (!projectId || !forDate) return;
    setPrefillBusy(true);
    try {
      const res = await authFetch(
        `${API}/projects/${projectId}/daily-reports/prefill/${forDate}`
      );
      if (!res.ok) {
        if (!isInitial) toast.error("Vorausfüllen fehlgeschlagen");
        return;
      }
      const data = await res.json();
      const pf = (data.personnel || []).map((p, i) => ({
        _key: `p-${Date.now()}-${i}`,
        user_id: p.user_id,
        name: p.name,
        company: p.company,
        work_start: p.work_start || "",
        work_end: p.work_end || "",
        break_minutes: p.break_minutes || 30,
      }));
      setPersonnel(pf);

      const t = (data.personnel || [])
        .filter((p) => p.hin || p.rueck)
        .map((p, i) => ({
          _key: `t-${Date.now()}-${i}`,
          user_id: p.user_id,
          user_name: p.name,
          hin_start: p.hin?.start || "",
          hin_end: p.hin?.end || "",
          hin_km: p.hin?.km || 0,
          rueck_start: p.rueck?.start || "",
          rueck_end: p.rueck?.end || "",
          rueck_km: p.rueck?.km || 0,
        }));
      setTrips(t);

      setAccommodations(data.accommodations || []);
      if ((data.accommodations || []).length > 0) setAccommodationActive(true);

      // Auto-open accordions where data exists
      const open = [];
      if (t.length) open.push("trips");
      if ((data.accommodations || []).length) open.push("acc");
      if ((data.totals?.vma_total_eur || 0) > 0) open.push("vma");
      setAccordionOpen(open);

      if (!isInitial) {
        toast.success(
          `Aus Stundenkonto übernommen: ${pf.length} Mitarbeiter, ${t.length} Fahrten`
        );
      }
    } catch {
      if (!isInitial) toast.error("Vorausfüllen fehlgeschlagen");
    } finally {
      setPrefillBusy(false);
    }
  }

  // ===== Calculated totals =====
  const totals = useMemo(() => {
    let workMin = 0;
    let travelMin = 0;
    let vmaSum = 0;
    let kmSum = 0;
    const tripByUser = new Map(trips.map((t) => [t.user_id, t]));
    personnel.forEach((p) => {
      const wm = minutesBetween(p.work_start, p.work_end, p.break_minutes);
      workMin += wm;
      const t = tripByUser.get(p.user_id);
      const hinMin = t ? minutesBetween(t.hin_start, t.hin_end, 0) : 0;
      const rueMin = t ? minutesBetween(t.rueck_start, t.rueck_end, 0) : 0;
      travelMin += hinMin + rueMin;
      kmSum += Number(t?.hin_km || 0) + Number(t?.rueck_km || 0);
      // Presence for VMA: from first travel start to last travel end (or work times)
      const firstTime = t?.hin_start || p.work_start;
      const lastTime = t?.rueck_end || p.work_end;
      const presence = minutesBetween(firstTime, lastTime, 0);
      vmaSum += vmaForMinutes(presence);
    });
    return {
      work_minutes: workMin,
      travel_minutes: travelMin,
      vma_eur: vmaSum,
      km: kmSum,
    };
  }, [personnel, trips]);

  // ===== Personnel handlers =====
  const addPerson = () => {
    setPersonnel((prev) => [
      ...prev,
      {
        _key: `p-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        user_id: "",
        name: "",
        work_start: "07:00",
        work_end: "16:00",
        break_minutes: 30,
      },
    ]);
  };
  const updatePerson = (key, next) =>
    setPersonnel((prev) => prev.map((p) => (p._key === key ? next : p)));
  const removePerson = (key) =>
    setPersonnel((prev) => prev.filter((p) => p._key !== key));

  // ===== Activity handlers =====
  const addActivity = () => {
    setActivities((prev) => [
      ...prev,
      {
        _key: `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        title: "",
        description: "",
      },
    ]);
  };
  const updateActivity = (key, next) =>
    setActivities((prev) => prev.map((a) => (a._key === key ? next : a)));
  const removeActivity = (key) =>
    setActivities((prev) => prev.filter((a) => a._key !== key));

  // ===== Trip handlers =====
  const updateTrip = (key, next) =>
    setTrips((prev) => prev.map((t) => (t._key === key ? next : t)));
  const addTrip = () => {
    setTrips((prev) => [
      ...prev,
      {
        _key: `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        user_id: "",
        user_name: "",
        hin_start: "",
        hin_end: "",
        hin_km: 0,
        rueck_start: "",
        rueck_end: "",
        rueck_km: 0,
      },
    ]);
  };
  const removeTrip = (key) =>
    setTrips((prev) => prev.filter((t) => t._key !== key));

  // ===== Photo upload =====
  // Spec Ronny 2026-06-29: max 10 allgemeine Fotos + 10 Störungsfotos (separat)
  const MAX_PHOTOS = 10;
  const MAX_DISRUPTION_PHOTOS = 10;
  const onPickPhotos = () => fileInputRef.current?.click();
  const onPhotosSelected = async (e) => {
    const allFiles = Array.from(e.target.files || []);
    if (!allFiles.length) return;

    // Bestehende Berichte mit >10 Fotos: keine neuen erlaubt
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Maximal ${MAX_PHOTOS} Fotos pro Bautagesbericht möglich.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      return;
    }

    // Multi-Select trimmen: nur so viele wie freie Slots
    const freeSlots = MAX_PHOTOS - photos.length;
    const files = allFiles.slice(0, freeSlots);
    const rejected = allFiles.length - files.length;

    setPhotoBusy(true);
    let successCount = 0;
    let firstError = "";
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const url = `${API}/projects/${projectId}/daily-reports/upload-photo?kind=photos${reportId ? `&report_id=${reportId}` : ""}`;
        const res = await fetch(url, {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
          body: fd,
        });
        if (!res.ok) {
          let detail = "";
          try { const j = await res.json(); detail = j?.detail || ""; } catch { /* ignore parse */ }
          if (!firstError) firstError = detail || `HTTP ${res.status}`;
          continue;
        }
        const data = await res.json();
        setPhotos((prev) => [
          ...prev,
          {
            id: data.id,
            url: data.url,
            thumbnail_url: data.thumbnail_url,
            filename: data.filename,
            storage_path: data.storage_path,
            size: data.size,
            original_size: data.original_size,
            width: data.width,
            height: data.height,
          },
        ]);
        successCount += 1;
      }

      if (successCount > 0) {
        const msg = rejected > 0
          ? `Nur ${successCount} von ${allFiles.length} Fotos hochgeladen. Maximal ${MAX_PHOTOS} Fotos pro Bautagesbericht möglich.`
          : `${successCount} Foto(s) hochgeladen${successCount === files.length ? "" : ` (${files.length - successCount} fehlgeschlagen)`}`;
        if (rejected > 0) toast.warning(msg); else toast.success(msg);
      }
      if (firstError && successCount === 0) {
        toast.error(`Foto-Upload fehlgeschlagen: ${firstError}`);
      }
    } catch (err) {
      toast.error(`Foto-Upload fehlgeschlagen: ${err?.message || ""}`);
    } finally {
      setPhotoBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };
  const removePhoto = (id) =>
    setPhotos((prev) => prev.filter((p) => p.id !== id));

  // ===== Disruption Photo upload (eigene Sektion) =====
  const onPickDisruptionPhotos = () => disruptionFileInputRef.current?.click();
  const onDisruptionPhotosSelected = async (e) => {
    const allFiles = Array.from(e.target.files || []);
    if (!allFiles.length) return;

    if (disruptionPhotos.length >= MAX_DISRUPTION_PHOTOS) {
      toast.error(`Maximal ${MAX_DISRUPTION_PHOTOS} Störungsfotos pro Bautagesbericht möglich.`);
      if (disruptionFileInputRef.current) disruptionFileInputRef.current.value = "";
      return;
    }
    const freeSlots = MAX_DISRUPTION_PHOTOS - disruptionPhotos.length;
    const files = allFiles.slice(0, freeSlots);
    const rejected = allFiles.length - files.length;

    setDisruptionPhotoBusy(true);
    let successCount = 0;
    let firstError = "";
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const url = `${API}/projects/${projectId}/daily-reports/upload-photo?kind=disruption_photos${reportId ? `&report_id=${reportId}` : ""}`;
        const res = await fetch(url, {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
          body: fd,
        });
        if (!res.ok) {
          let detail = "";
          try { const j = await res.json(); detail = j?.detail || ""; } catch { /* ignore parse */ }
          if (!firstError) firstError = detail || `HTTP ${res.status}`;
          continue;
        }
        const data = await res.json();
        setDisruptionPhotos((prev) => [
          ...prev,
          {
            id: data.id,
            url: data.url,
            thumbnail_url: data.thumbnail_url,
            filename: data.filename,
            storage_path: data.storage_path,
            caption: "",
          },
        ]);
        successCount += 1;
      }
      if (successCount > 0) {
        const msg = rejected > 0
          ? `Nur ${successCount} von ${allFiles.length} Störungsfotos hochgeladen. Maximal ${MAX_DISRUPTION_PHOTOS} möglich.`
          : `${successCount} Störungs-Foto(s) hochgeladen`;
        if (rejected > 0) toast.warning(msg); else toast.success(msg);
      }
      if (firstError && successCount === 0) {
        toast.error(`Foto-Upload fehlgeschlagen: ${firstError}`);
      }
    } catch (err) {
      toast.error(`Foto-Upload fehlgeschlagen: ${err?.message || ""}`);
    } finally {
      setDisruptionPhotoBusy(false);
      if (disruptionFileInputRef.current) disruptionFileInputRef.current.value = "";
    }
  };
  const removeDisruptionPhoto = (id) =>
    setDisruptionPhotos((prev) => prev.filter((p) => p.id !== id));

  // ===== Save =====
  const onSave = async () => {
    if (!canSave) {
      toast.error("Keine Berechtigung zum Speichern von Bautagesberichten");
      return;
    }
    const personnelClean = personnel
      .filter((p) => p.user_id)
      .map((p) => ({
        user_id: p.user_id,
        name: p.name,
        company: p.company,
        work_start: p.work_start,
        work_end: p.work_end,
        break_minutes: p.break_minutes,
        work_minutes: minutesBetween(p.work_start, p.work_end, p.break_minutes),
      }));
    if (personnelClean.length === 0) {
      toast.error("Mindestens ein Mitarbeiter erforderlich");
      return;
    }
    const activitiesClean = activities
      .filter((a) => a.title?.trim() || a.description?.trim())
      .map((a) => {
        const cleanDesc = stripDuplicateTitleFromDescription(a.title, a.description || "");
        return {
          description: cleanDesc,
          hours: 0,
          source_task_id: a.source_task_id || null,
          source_task_title: (a.title || a.source_task_title || "").trim() || null,
          employee_id: a.employee_id || null,
          employee_name: a.employee_name || null,
          area_location: (a.area_location || "").trim() || null,
        };
      });
    const tripsClean = trips
      .filter(
        (t) =>
          t.user_id && (t.hin_start || t.hin_end || t.rueck_start || t.rueck_end)
      )
      .map((t) => ({
        user_id: t.user_id,
        user_name: t.user_name,
        hin_start: t.hin_start || "",
        hin_end: t.hin_end || "",
        hin_km: Number(t.hin_km || 0),
        rueck_start: t.rueck_start || "",
        rueck_end: t.rueck_end || "",
        rueck_km: Number(t.rueck_km || 0),
      }));

    const payload = {
      project_id: projectId,
      date,
      weather,
      temperature_celsius: temperature === "" ? null : Number(temperature),
      notes,
      disruptions,
      disruption_area_location: disruptionAreaLocation || null,
      activities: activitiesClean,
      personnel: personnelClean,
      trips: tripsClean,
      accommodation_active: accommodationActive,
      accommodation_ids: accommodations.map((a) => a.id).filter(Boolean),
      vma_total_eur: totals.vma_eur,
      include_trips_in_pdf: includeTripsInPdf,
      include_vma_in_pdf: includeVmaInPdf,
      include_accommodation_in_pdf: includeAccommodationInPdf,
      include_disruption_in_pdf: includeDisruptionInPdf,
      include_disruption_photos_in_ba: includeDisruptionPhotosInBa,
      show_vob_notice: showVobNotice,
      signature_employee: signatureEmployeeName,
      signature_customer: signatureCustomerName,
      signature_location: signatureLocation,
      signature_date: signatureDate,
      signature_employee_image: signatureEmployeeImage || null,
      signature_customer_image: signatureCustomerImage || null,
      photos: photos.map((p) => ({
        id: p.id,
        url: p.url,
        filename: p.filename,
        storage_path: p.storage_path,
        caption: p.caption || "",
      })),
      disruption_photos: disruptionPhotos.map((p) => ({
        id: p.id,
        url: p.url,
        filename: p.filename,
        storage_path: p.storage_path,
        caption: p.caption || "",
      })),
    };

    setSaving(true);
    try {
      let res;
      if (reportId) {
        res = await authFetch(
          `${API}/projects/${projectId}/daily-reports/${reportId}`,
          { method: "PATCH", body: JSON.stringify(payload) }
        );
      } else {
        res = await authFetch(`${API}/projects/${projectId}/daily-reports`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Speichern fehlgeschlagen");
      }
      toast.success(reportId ? "Bericht aktualisiert" : "Bericht erstellt");
      navigate(`/app/projekte/${projectId}/berichte`);
    } catch (e) {
      toast.error(e.message || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Projekt nicht gefunden
        </h2>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
        </Button>
      </div>
    );
  }

  const weekday = format(parseISO(date), "EEEE", { locale: de });
  const kw = getISOWeek(parseISO(date));

  return (
    <div
      className="min-h-screen bg-gray-50 pb-32"
      data-testid="btb-form-page"
    >
      {/* ===== STICKY HEADER ===== */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/app/projekte/${projectId}/berichte`)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            data-testid="btb-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-gray-500 truncate">{project.name}</div>
            <h1 className="text-lg md:text-xl font-bold text-gray-900 font-[Manrope] flex items-center gap-2 flex-wrap">
              <span>{reportId ? "Bericht bearbeiten" : "Neuer Bautagesbericht"}</span>
              {reportNumber && (
                <span
                  className="text-[11px] font-mono px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200"
                  data-testid="btb-report-number"
                >
                  {reportNumber}
                </span>
              )}
            </h1>
          </div>
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="max-w-4xl mx-auto px-3 md:px-6 py-4 md:py-6 space-y-5">
        {/* === A. KOPFBEREICH === */}
        <section className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
              Kopfbereich
            </h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => runPrefill(date)}
              disabled={prefillBusy}
              data-testid="btb-prefill-btn"
            >
              {prefillBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Aus Stundenkonto laden
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Datum</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 h-11"
                data-testid="btb-date"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Wochentag</Label>
              <div className="mt-1 h-11 px-3 rounded-lg bg-gray-50 border border-gray-200 flex items-center capitalize text-gray-900">
                {weekday}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">KW</Label>
              <div className="mt-1 h-11 px-3 rounded-lg bg-gray-50 border border-gray-200 flex items-center text-gray-900">
                {kw}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Wetter</Label>
              <Select value={weather} onValueChange={setWeather}>
                <SelectTrigger className="mt-1 h-11" data-testid="btb-weather">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WEATHER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      <div className="flex items-center gap-2">
                        <o.icon className="w-4 h-4" />
                        {o.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* === B. PERSONAL + ARBEITSZEIT (Kern) === */}
        <section className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              <h2 className="text-base md:text-lg font-bold text-gray-900">
                Personal & Arbeitszeit
              </h2>
              <Badge variant="outline" className="ml-1">
                {personnel.length}
              </Badge>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={addPerson}
              data-testid="btb-add-person-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Mitarbeiter
            </Button>
          </div>

          {personnel.length === 0 ? (
            <div className="text-sm text-gray-500 italic py-4 text-center">
              Noch keine Mitarbeiter erfasst. Klicke "Mitarbeiter" oder lade aus
              dem Stundenkonto.
            </div>
          ) : (
            <div className="space-y-3">
              {personnel.map((p) => (
                <PersonRow
                  key={p._key}
                  entry={p}
                  users={users}
                  onChange={(next) => updatePerson(p._key, next)}
                  onRemove={() => removePerson(p._key)}
                />
              ))}
            </div>
          )}

          {/* Total */}
          <div className="pt-3 border-t flex items-center justify-between text-sm">
            <span className="text-gray-500">Gesamt Arbeitszeit</span>
            <span className="text-base font-bold text-gray-900">
              {fmtH(totals.work_minutes)}
            </span>
          </div>
        </section>

        {/* === C. TÄTIGKEITEN === */}
        <section className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-blue-600" />
              <h2 className="text-base md:text-lg font-bold text-gray-900">
                Tätigkeiten
              </h2>
              <Badge variant="outline" className="ml-1">
                {activities.length}
              </Badge>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={addActivity}
              data-testid="btb-add-activity-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Tätigkeit
            </Button>
          </div>

          {activities.length === 0 ? (
            <div className="text-sm text-gray-500 italic py-4 text-center">
              Noch keine Tätigkeiten. Klicke "Tätigkeit" und nutze die KI für
              VOB-konforme Formulierungen.
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((a, idx) => (
                <ActivityCard
                  key={a._key}
                  idx={idx}
                  activity={a}
                  onChange={(next) => updateActivity(a._key, next)}
                  onRemove={() => removeActivity(a._key)}
                  projectId={projectId}
                  reportDate={date}
                  areaSuggestions={areaSuggestions}
                />
              ))}
            </div>
          )}
        </section>

        {/* === D. BESONDERHEITEN === */}
        <section className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h2 className="text-base md:text-lg font-bold text-gray-900">
                Besonderheiten / Störungen
              </h2>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!disruptions?.trim()) {
                  toast.info("Bitte zuerst Stichpunkte eingeben");
                  return;
                }
                setDisruptionBusy(true);
                try {
                  const res = await authFetch(
                    `${API}/projects/${projectId}/daily-reports/formulate-disruption`,
                    { method: "POST", body: JSON.stringify({
                      text: disruptions,
                      report_date: date,
                      area_location: disruptionAreaLocation || "",
                    }) }
                  );
                  if (!res.ok) throw new Error(await res.text());
                  const data = await res.json();
                  if (data?.text) {
                    setDisruptions(data.text);
                    toast.success("VOB-Text formuliert");
                  } else {
                    toast.error("Kein Text zurückgegeben");
                  }
                } catch {
                  toast.error("KI-Formulierung fehlgeschlagen");
                } finally {
                  setDisruptionBusy(false);
                }
              }}
              disabled={disruptionBusy}
              data-testid="btb-disruption-ai-btn"
            >
              {disruptionBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              VOB-Text formulieren
            </Button>
          </div>
          {/* Bereich / Ort der Störung */}
          <div>
            <Label className="text-xs text-gray-500">Bereich / Ort der Störung (optional)</Label>
            <Input
              value={disruptionAreaLocation}
              onChange={(e) => setDisruptionAreaLocation(e.target.value)}
              placeholder="z. B. Anlieferung, Hinterer Technik Bereich, Achse 3-5 ..."
              list="btb-disruption-area-suggestions"
              className="mt-1"
              data-testid="btb-disruption-area"
            />
            {areaSuggestions.length > 0 && (
              <datalist id="btb-disruption-area-suggestions">
                {areaSuggestions.map((a) => <option key={a} value={a} />)}
              </datalist>
            )}
          </div>
          <Textarea
            value={disruptions}
            onChange={(e) => {
              setDisruptions(e.target.value);
              // Auto: sobald User Text eintippt, Disruption-PDF-Anzeige automatisch aktivieren
              if (e.target.value.trim() && includeDisruptionInPdf !== true) {
                setIncludeDisruptionInPdf(true);
              }
            }}
            rows={4}
            placeholder={
              "Störung kurz beschreiben – Deutsch, Englisch oder Slowakisch:\n" +
              "- Materiallieferung 6h verspätet, 4h Stillstand\n" +
              "- delivery area blocked by material, 2h delay\n" +
              "- material blokoval vstup, 2h meskanie"
            }
            data-testid="btb-disruptions"
          />
          <div className="text-xs text-gray-500 -mt-1">
            Störung auch auf <strong>Deutsch, Englisch oder Slowakisch</strong> – die KI formuliert daraus einen rechtssicheren deutschen VOB-Text.
          </div>
          {/* PDF-Sichtbarkeit für Störungen + VOB-Hinweis */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeDisruptionInPdf === true ||
                  (includeDisruptionInPdf === null && !!disruptions.trim())}
                onChange={(e) => setIncludeDisruptionInPdf(e.target.checked)}
                className="w-4 h-4"
                data-testid="btb-include-disruption-toggle"
              />
              <span className="text-sm text-gray-700">
                Störungen / Besonderheiten im PDF anzeigen
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showVobNotice === true ||
                  (showVobNotice === null && !!disruptions.trim() &&
                    !disruptions.toLowerCase().includes("keine behinderungen"))}
                onChange={(e) => setShowVobNotice(e.target.checked)}
                className="w-4 h-4"
                data-testid="btb-show-vob-notice-toggle"
              />
              <span className="text-sm text-gray-700">
                Rechtlichen Hinweis nach VOB im PDF einblenden
              </span>
            </label>
          </div>
          <div className="text-xs text-gray-500">
            Tipp: Mehrsprachige Eingabe möglich. Die KI erkennt die Sprache, übersetzt ins Deutsche und formuliert einen rechtssicheren VOB/B § 6 konformen Text.
          </div>

          {/* === Fotos zur Störung === */}
          <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3" data-testid="btb-disruption-photos-section">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ImagePlus className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-gray-900">Fotos zur Störung</h3>
                <Badge
                  variant={disruptionPhotos.length >= MAX_DISRUPTION_PHOTOS ? "destructive" : "outline"}
                  className="ml-1 font-mono"
                  data-testid="btb-disruption-photos-counter"
                >
                  {disruptionPhotos.length} / {MAX_DISRUPTION_PHOTOS}
                </Badge>
              </div>
            </div>
            {disruptionPhotos.length >= MAX_DISRUPTION_PHOTOS && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="btb-disruption-photos-limit-hint">
                Maximale Anzahl Störungsfotos erreicht ({disruptionPhotos.length} / {MAX_DISRUPTION_PHOTOS}).
              </div>
            )}
            <input
              type="file"
              ref={disruptionFileInputRef}
              accept="image/*"
              multiple
              className="hidden"
              onChange={onDisruptionPhotosSelected}
              data-testid="btb-disruption-photo-input"
              disabled={disruptionPhotos.length >= MAX_DISRUPTION_PHOTOS}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onPickDisruptionPhotos}
              disabled={disruptionPhotoBusy || disruptionPhotos.length >= MAX_DISRUPTION_PHOTOS}
              className="w-full h-10"
              data-testid="btb-disruption-photo-btn"
            >
              {disruptionPhotoBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ImagePlus className="w-4 h-4 mr-2" />
              )}
              {disruptionPhotos.length >= MAX_DISRUPTION_PHOTOS
                ? "Maximale Anzahl erreicht"
                : "+ Fotos zur Störung hinzufügen"}
            </Button>
            {disruptionPhotos.length > 0 && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {disruptionPhotos.map((ph, idx) => (
                    <div
                      key={ph.id}
                      className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 space-y-1"
                      data-testid={`btb-disruption-photo-card-${ph.id}`}
                    >
                      <div className="relative aspect-[4/3] bg-gray-100 flex items-center justify-center">
                        <img
                          src={photoSrc(ph)}
                          alt={ph.filename || `Störungs-Foto ${idx + 1}`}
                          className="max-w-full max-h-full object-contain"
                          loading="lazy"
                        />
                        <button
                          type="button"
                          onClick={() => removeDisruptionPhoto(ph.id)}
                          className="absolute top-1 right-1 p-1.5 bg-black/70 text-white rounded-full hover:bg-red-600"
                          aria-label="Foto entfernen"
                          data-testid={`btb-disruption-photo-remove-${ph.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <Input
                        type="text"
                        value={ph.caption || ""}
                        onChange={(e) => {
                          const newCap = e.target.value;
                          setDisruptionPhotos((prev) =>
                            prev.map((p) => (p.id === ph.id ? { ...p, caption: newCap } : p))
                          );
                        }}
                        placeholder="Bildunterschrift (optional)"
                        className="h-8 text-xs border-0 rounded-none border-t border-gray-200 bg-white"
                        data-testid={`btb-disruption-photo-caption-${ph.id}`}
                      />
                    </div>
                  ))}
                </div>
                <label className="flex items-start gap-2 pt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeDisruptionPhotosInBa === true ||
                      (includeDisruptionPhotosInBa === null && disruptionPhotos.length > 0)}
                    onChange={(e) => setIncludeDisruptionPhotosInBa(e.target.checked)}
                    className="w-4 h-4 mt-0.5"
                    data-testid="btb-include-disruption-photos-ba-toggle"
                  />
                  <span className="text-sm text-gray-700">
                    Fotos zur Störung in Behinderungsanzeige übernehmen
                  </span>
                </label>
              </>
            )}
          </div>
          {reportId && disruptions?.trim() && (
            <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-red-900">
                  Behinderungsanzeige nach § 6 Abs. 1 VOB/B
                </div>
                <div className="text-xs text-red-700 mt-0.5">
                  Erzeugt ein separates rechtssicheres PDF zum Versand an den Auftraggeber.
                  Eigene Nummerierung (BA-...). Versand per Outlook.
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    toast.loading("Behinderungsanzeige wird erstellt...", { id: `ba-${reportId}` });
                    const res = await authFetch(`${API}/daily-reports/${reportId}/behinderungsanzeige`);
                    if (!res.ok) {
                      const err = await res.json().catch(() => ({}));
                      throw new Error(err.detail || "Fehler");
                    }
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    // Dateiname aus Content-Disposition (sprechend vom Backend)
                    let filename = `Behinderungsanzeige_${date}.pdf`;
                    const cd = res.headers.get("content-disposition") || "";
                    const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
                    const asciiMatch = cd.match(/filename="?([^";]+)"?/i);
                    if (utf8Match) {
                      try { filename = decodeURIComponent(utf8Match[1]); } catch { /* keep */ }
                    } else if (asciiMatch) {
                      filename = asciiMatch[1];
                    }
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 5000);
                    toast.success("Behinderungsanzeige heruntergeladen", { id: `ba-${reportId}` });
                  } catch (e) {
                    toast.error(e.message || "Erstellung fehlgeschlagen", { id: `ba-${reportId}` });
                  }
                }}
                className="shrink-0 border-red-300 text-red-700 hover:bg-red-100"
                data-testid="btb-behinderungsanzeige-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                BA erstellen
              </Button>
            </div>
          )}
          {!reportId && disruptions?.trim() && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              💡 Speichere den Bericht zuerst, dann kannst du eine Behinderungsanzeige erstellen.
            </div>
          )}
        </section>

        {/* === OPTIONAL ACCORDIONS === */}
        <section className="bg-white rounded-2xl border border-gray-200 p-2 md:p-3">
          <Accordion
            type="multiple"
            value={accordionOpen}
            onValueChange={setAccordionOpen}
            className="w-full"
          >
            {/* FAHRTEN */}
            <AccordionItem value="trips" className="border-b last:border-b-0">
              <AccordionTrigger
                className="px-3 hover:no-underline"
                data-testid="btb-acc-trips"
              >
                <div className="flex items-center gap-2 text-base font-semibold text-gray-900">
                  <Car className="w-5 h-5 text-blue-600" />
                  Fahrten
                  <Badge variant="outline" className="ml-1">
                    {trips.length} Einträge
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 space-y-3">
                {trips.length === 0 && (
                  <div className="text-sm text-gray-500 italic">
                    Keine Fahrten erfasst.
                  </div>
                )}
                {trips.map((t) => {
                  const userName =
                    users.find((u) => u.id === t.user_id)?.name ||
                    t.user_name ||
                    "—";
                  return (
                    <div
                      key={t._key}
                      className="rounded-xl border border-gray-200 p-3 space-y-2"
                      data-testid={`btb-trip-${t._key}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-gray-900">
                          {userName}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeTrip(t._key)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs text-gray-500">
                            Hinfahrt Start
                          </Label>
                          <Input
                            type="time"
                            value={t.hin_start || ""}
                            onChange={(e) =>
                              updateTrip(t._key, {
                                ...t,
                                hin_start: e.target.value,
                              })
                            }
                            className="mt-1 h-11"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">
                            Hinfahrt Ende
                          </Label>
                          <Input
                            type="time"
                            value={t.hin_end || ""}
                            onChange={(e) =>
                              updateTrip(t._key, {
                                ...t,
                                hin_end: e.target.value,
                              })
                            }
                            className="mt-1 h-11"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">
                            Hinfahrt km
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={t.hin_km || 0}
                            onChange={(e) =>
                              updateTrip(t._key, {
                                ...t,
                                hin_km: e.target.value,
                              })
                            }
                            className="mt-1 h-11"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">
                            Rückfahrt Start
                          </Label>
                          <Input
                            type="time"
                            value={t.rueck_start || ""}
                            onChange={(e) =>
                              updateTrip(t._key, {
                                ...t,
                                rueck_start: e.target.value,
                              })
                            }
                            className="mt-1 h-11"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">
                            Rückfahrt Ende
                          </Label>
                          <Input
                            type="time"
                            value={t.rueck_end || ""}
                            onChange={(e) =>
                              updateTrip(t._key, {
                                ...t,
                                rueck_end: e.target.value,
                              })
                            }
                            className="mt-1 h-11"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">
                            Rückfahrt km
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={t.rueck_km || 0}
                            onChange={(e) =>
                              updateTrip(t._key, {
                                ...t,
                                rueck_km: e.target.value,
                              })
                            }
                            className="mt-1 h-11"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTrip}
                  data-testid="btb-add-trip-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Fahrt hinzufügen
                </Button>
                <div className="text-sm text-gray-500 pt-2 border-t">
                  Gesamt: {fmtH(totals.travel_minutes)} ·{" "}
                  {totals.km.toFixed(1).replace(".", ",")} km
                </div>
                <label className="flex items-center gap-2 pt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeTripsInPdf}
                    onChange={(e) => setIncludeTripsInPdf(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="btb-include-trips-toggle"
                  />
                  <span className="text-sm text-gray-700">
                    Fahrten im PDF-Bautagesbericht einbeziehen
                  </span>
                </label>
              </AccordionContent>
            </AccordionItem>

            {/* UNTERKUNFT */}
            <AccordionItem value="acc" className="border-b last:border-b-0">
              <AccordionTrigger
                className="px-3 hover:no-underline"
                data-testid="btb-acc-accommodation"
              >
                <div className="flex items-center gap-2 text-base font-semibold text-gray-900">
                  <BedDouble className="w-5 h-5 text-blue-600" />
                  Unterkunft
                  <Badge variant="outline" className="ml-1">
                    {accommodations.length} Einträge
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 space-y-2">
                {accommodations.length === 0 && (
                  <div className="text-sm text-gray-500 italic">
                    Keine Unterkunft hinterlegt.
                  </div>
                )}
                {accommodations.map((a, idx) => (
                  <div
                    key={a.id || idx}
                    className="rounded-lg border border-gray-200 p-3 bg-gray-50"
                  >
                    <div className="font-medium text-gray-900">{a.name}</div>
                    {a.address && (
                      <div className="text-sm text-gray-600">{a.address}</div>
                    )}
                    {a.relevant_user_names?.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Für: {a.relevant_user_names.join(", ")}
                      </div>
                    )}
                  </div>
                ))}
                <label className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    checked={accommodationActive}
                    onChange={(e) => setAccommodationActive(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="btb-accommodation-toggle"
                  />
                  <span className="text-sm text-gray-700">
                    Übernachtung aktiv (für diesen Tag)
                  </span>
                </label>
                <label className="flex items-center gap-2 pt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAccommodationInPdf}
                    onChange={(e) => setIncludeAccommodationInPdf(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="btb-include-accommodation-toggle"
                  />
                  <span className="text-sm text-gray-700">
                    Unterkunft im PDF-Bautagesbericht einbeziehen
                  </span>
                </label>
              </AccordionContent>
            </AccordionItem>

            {/* VMA */}
            <AccordionItem value="vma" className="border-b-0">
              <AccordionTrigger
                className="px-3 hover:no-underline"
                data-testid="btb-acc-vma"
              >
                <div className="flex items-center gap-2 text-base font-semibold text-gray-900">
                  <Wallet className="w-5 h-5 text-blue-600" />
                  VMA (Verpflegungspauschale)
                  <Badge variant="outline" className="ml-1">
                    {totals.vma_eur.toFixed(2).replace(".", ",")} €
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 space-y-2 text-sm text-gray-700">
                <p className="text-gray-600">
                  Automatisch berechnet basierend auf Anwesenheit:
                </p>
                <ul className="ml-4 list-disc text-gray-600">
                  <li>weniger als 8 Stunden: 0 €</li>
                  <li>8 bis unter 24 Stunden: 14 €</li>
                  <li>24 Stunden oder mehr: 28 €</li>
                </ul>
                <div className="pt-2 border-t font-semibold">
                  Gesamt: {totals.vma_eur.toFixed(2).replace(".", ",")} €
                </div>
                <label className="flex items-center gap-2 pt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeVmaInPdf}
                    onChange={(e) => setIncludeVmaInPdf(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="btb-include-vma-toggle"
                  />
                  <span className="text-sm text-gray-700">
                    VMA im PDF-Bautagesbericht einbeziehen
                  </span>
                </label>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>

        {/* === FOTOS / FOTODOKUMENTATION (eigener prominent sichtbarer Bereich) === */}
        <section
          className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 space-y-3"
          data-testid="btb-photos-section"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ImagePlus className="w-5 h-5 text-blue-600" />
              <h2 className="text-base md:text-lg font-bold text-gray-900">
                Fotos / Fotodokumentation
              </h2>
              <Badge
                variant={photos.length >= MAX_PHOTOS ? "destructive" : "outline"}
                className="ml-1 font-mono"
                data-testid="btb-photos-counter"
              >
                {photos.length} / {MAX_PHOTOS}
              </Badge>
            </div>
            <span className="text-[11px] text-gray-500 hidden sm:inline">
              Fotos werden automatisch komprimiert (max. 1600 px, JPEG)
            </span>
          </div>

          {photos.length >= MAX_PHOTOS && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="btb-photos-limit-hint">
              Maximale Anzahl erreicht ({photos.length} / {MAX_PHOTOS}). Bitte ein Foto entfernen, bevor weitere hinzugefügt werden.
            </div>
          )}

          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            multiple
            className="hidden"
            onChange={onPhotosSelected}
            data-testid="btb-photo-input"
            disabled={photos.length >= MAX_PHOTOS}
          />
          <input
            type="file"
            ref={cameraInputRef}
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={onPhotosSelected}
            data-testid="btb-camera-input"
            disabled={photos.length >= MAX_PHOTOS}
          />

          {/* Großer Upload-Bereich mit Drag & Drop */}
          <div
            className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
              photos.length >= MAX_PHOTOS
                ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
                : "border-blue-200 bg-blue-50/40 hover:border-blue-400 hover:bg-blue-50 cursor-pointer"
            }`}
            onClick={photos.length >= MAX_PHOTOS ? undefined : onPickPhotos}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={async (e) => {
              e.preventDefault(); e.stopPropagation();
              if (photos.length >= MAX_PHOTOS) return;
              const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
              if (files.length === 0) return;
              await onPhotosSelected({ target: { files } });
            }}
            data-testid="btb-photo-dropzone"
          >
            <ImagePlus className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-700">
              {photos.length >= MAX_PHOTOS ? "Maximale Anzahl erreicht" : "+ Fotos hinzufügen"}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {photos.length >= MAX_PHOTOS
                ? "Bitte zuerst ein Foto entfernen"
                : "Klicken zum Auswählen oder Dateien hierher ziehen"}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="default"
              onClick={() => cameraInputRef.current?.click()}
              disabled={photoBusy || photos.length >= MAX_PHOTOS}
              className="h-11"
              data-testid="btb-camera-btn"
            >
              {photoBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Camera className="w-4 h-4 mr-2" />
              )}
              Foto aufnehmen
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onPickPhotos}
              disabled={photoBusy || photos.length >= MAX_PHOTOS}
              className="h-11"
              data-testid="btb-photo-btn"
            >
              {photoBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ImagePlus className="w-4 h-4 mr-2" />
              )}
              Aus Galerie
            </Button>
          </div>

          {photos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
              {photos.map((ph, idx) => (
                <div
                  key={ph.id}
                  className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 space-y-1"
                  data-testid={`btb-photo-card-${ph.id}`}
                >
                  <div className="relative aspect-[4/3] bg-gray-100 flex items-center justify-center">
                    <img
                      src={photoSrc(ph)}
                      alt={ph.filename || `Foto ${idx + 1}`}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(ph.id)}
                      className="absolute top-1 right-1 p-1.5 bg-black/70 text-white rounded-full hover:bg-red-600"
                      aria-label="Foto entfernen"
                      data-testid={`btb-photo-remove-${ph.id}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <Input
                    type="text"
                    value={ph.caption || ""}
                    onChange={(e) => {
                      const newCap = e.target.value;
                      setPhotos((prev) =>
                        prev.map((p) => (p.id === ph.id ? { ...p, caption: newCap } : p))
                      );
                    }}
                    placeholder="Bildunterschrift (optional)"
                    className="h-8 text-xs border-0 rounded-none border-t border-gray-200 bg-white"
                    data-testid={`btb-photo-caption-${ph.id}`}
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* === ABNAHME / UNTERSCHRIFTEN === */}
        <section
          className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 space-y-4"
          data-testid="btb-signatures-section"
        >
          <div className="flex items-center gap-2">
            <Pen className="w-5 h-5 text-blue-600" />
            <h2 className="text-base md:text-lg font-bold text-gray-900">
              Abnahme / Unterschriften
            </h2>
          </div>

          {/* Namen */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-600">
                Bauleiter / Mitarbeiter (Name)
              </Label>
              <Input
                type="text"
                value={signatureEmployeeName}
                onChange={(e) => setSignatureEmployeeName(e.target.value)}
                placeholder="Vor- und Nachname"
                data-testid="btb-sig-employee-name"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">
                Auftraggeber / Bauherr (Name)
              </Label>
              <Input
                type="text"
                value={signatureCustomerName}
                onChange={(e) => setSignatureCustomerName(e.target.value)}
                placeholder="Vor- und Nachname"
                data-testid="btb-sig-customer-name"
              />
            </div>
          </div>

          {/* Ort + Datum */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-600">
                Ort (automatisch aus Projekt)
              </Label>
              <Input
                type="text"
                value={signatureLocation}
                onChange={(e) => setSignatureLocation(e.target.value)}
                placeholder="z.B. Stuttgart"
                data-testid="btb-sig-location"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">
                Datum (Berichtdatum)
              </Label>
              <Input
                type="date"
                value={signatureDate}
                onChange={(e) => setSignatureDate(e.target.value)}
                data-testid="btb-sig-date"
              />
            </div>
          </div>

          {/* Signatur-Pads */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SignaturePad
              label="Bauleiter / Mitarbeiter — digitale Unterschrift"
              value={signatureEmployeeImage}
              onChange={setSignatureEmployeeImage}
              testId="btb-sig-employee-pad"
            />
            <SignaturePad
              label="Auftraggeber / Bauherr — digitale Unterschrift"
              value={signatureCustomerImage}
              onChange={setSignatureCustomerImage}
              testId="btb-sig-customer-pad"
            />
          </div>

          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-2">
            Hinweis: Wird keine Unterschrift gezeichnet, erscheint im PDF ein
            digitaler Erstellvermerk („Bericht digital erstellt durch …").
            Unterschriften können später separat ergänzt werden.
          </div>
        </section>

        {/* === Notes (optional Footer Free Text) === */}
        <section className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 space-y-3">
          <Label className="text-sm font-semibold text-gray-900">
            Interne Notizen (optional)
          </Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Interne Anmerkungen, Hinweise für PDF-Export..."
            data-testid="btb-notes"
          />
        </section>
      </div>

      {/* ===== STICKY SAVE BAR ===== */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.05)]">
        <div className="max-w-4xl mx-auto px-3 md:px-6 py-3 flex items-center gap-3">
          <div className="flex-1 text-sm text-gray-600">
            <div className="font-semibold text-gray-900">
              {personnel.length} Mitarbeiter · {fmtH(totals.work_minutes)}
            </div>
            <div className="text-xs">
              Fahrten {fmtH(totals.travel_minutes)} · VMA{" "}
              {totals.vma_eur.toFixed(2).replace(".", ",")} €
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/app/projekte/${projectId}/berichte`)}
            data-testid="btb-cancel-btn"
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={saving || !canSave}
            title={!canSave ? "Keine Berechtigung zum Speichern von Bautagesberichten" : undefined}
            data-testid="btb-save-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}
